<template>
  <!-- Start Navbar -->
  <nav id="topnav" class="defaultscroll is-sticky tagline-height">
    <div class="container relative">
      <!-- Logo container-->
      <NuxtLink
        class="logo"
        :to="$localePath('index')"
        :aria-label="$t('footer.homeAria')"
      >
        <!-- Three theme variants (light scrolled / light top / dark) — only
             one is visible at a time. alt="" + aria-hidden="true" on all
             three so screen readers see the link's aria-label, not three
             "Georgia Dream" announcements. -->
        <span class="inline-block dark:hidden">
          <NuxtImg
            src="/images/logo-dark.png"
            width="215"
            height="28"
            loading="lazy"
            class="h-6 min-[576px]:h-7 l-dark"
            alt=""
            aria-hidden="true"
          />
          <NuxtImg
            src="/images/logo-light.png"
            width="215"
            height="28"
            loading="lazy"
            class="h-6 min-[576px]:h-7 l-light"
            alt=""
            aria-hidden="true"
          />
        </span>
        <NuxtImg
          src="/images/logo-white.png"
          width="215"
          height="28"
          loading="lazy"
          class="hidden dark:inline-block h-6 min-[576px]:h-7"
          alt=""
          aria-hidden="true"
        />
      </NuxtLink>
      <!-- End Logo container-->

      <!-- Start Mobile Toggle -->

      <div class="menu-extras">
        <div class="flex flex-row items-center">
          <!-- Utility controls (compact, mobile only — hidden when tagline is visible at ≥576px) -->
          <NavbarUtilityControls
            variant="light"
            compact
            class="mr-3 min-[576px]:hidden"
          />
          <!-- End utility controls -->
          <div class="menu-item">
            <button
              type="button"
              class="navbar-toggle"
              :class="{ open: isMenuOpen }"
              :aria-label="$t('nav.mobile.toggle')"
              aria-controls="navigation"
              :aria-expanded="isMenuOpen"
              @click="toggleMenu"
            >
              <div class="lines">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
        </div>
      </div>
      <!-- End Mobile Toggle -->

      <!-- Mobile drawer backdrop (visible only below 992px, styled in _topnav.css) -->
      <div
        class="nav-backdrop"
        :class="{ open: isMenuOpen }"
        aria-hidden="true"
        @click="closeMenu"
      ></div>

      <div id="navigation" :class="{ open: isMenuOpen }">
        <!-- Drawer close button (mobile only) -->
        <button
          type="button"
          class="nav-drawer-close"
          :aria-label="$t('nav.mobile.close')"
          @click="closeMenu"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <ul class="navigation-menu nav-right !justify-end nav-light">
          <li
            v-for="(item, index) in menu"
            :key="index"
            :class="{ 'has-submenu parent-parent-menu-item': item.children }"
          >
            <NuxtLink
              v-if="!item.children"
              :to="$localePath(item.link)"
              class="sub-menu-item"
              :aria-current="route.path === $localePath(item.link) ? 'page' : undefined"
            >
              {{ item.label }}
            </NuxtLink>

            <template v-else>
              <button
                type="button"
                class="sub-menu-item"
                :aria-expanded="openIndexes.main === index"
                aria-haspopup="true"
                @click="toggleMain(index)"
              >
                {{ item.label }}
              </button>
              <span class="menu-arrow"></span>
              <ul class="submenu" :class="{ open: openIndexes.main === index }">
                <li
                  v-for="(child, childIndex) in item.children"
                  :key="childIndex"
                  :class="{ 'has-submenu parent-menu-item': child.children }"
                >
                  <NuxtLink
                    v-if="!child.children"
                    :to="$localePath(child.link)"
                    class="sub-menu-item"
                  >
                    {{ child.label }}
                  </NuxtLink>

                  <template v-else>
                    <button
                      type="button"
                      class="sub-menu-item"
                      :aria-expanded="openIndexes.child === childIndex"
                      aria-haspopup="true"
                      @click="toggleChild(childIndex)"
                    >
                      {{ child.label }}
                    </button>
                    <span class="submenu-arrow"></span>
                    <ul
                      class="submenu"
                      :class="{ open: openIndexes.child === childIndex }"
                    >
                      <li
                        v-for="(subChild, subIndex) in child.children"
                        :key="subIndex"
                      >
                        <NuxtLink :to="$localePath(subChild.link)" class="sub-menu-item">
                          {{ subChild.label }}
                        </NuxtLink>
                      </li>
                    </ul>
                  </template>
                </li>
              </ul>
            </template>
          </li>
        </ul>

        <!-- Drawer contact card (mobile only) — mirrors the desktop tagline
             info that's hidden below 575px so guests still get address +
             socials inside the side menu. -->
        <NavbarDrawerContact @close="closeMenu" />

        <!-- Drawer footer with language + theme controls (mobile only).
             !mt-0 cancels the legacy mt-auto on the CSS class so the auto
             margin on .nav-drawer-contact is the single bottom-pinner —
             contact + footer then sit flush together at the drawer base. -->
        <div class="nav-drawer-footer !mt-0">
          <NavbarUtilityControls variant="light" />
        </div>
      </div>
      <!--end navigation-->
    </div>
    <!--end container-->
  </nav>
  <!--end header-->
  <!-- End Navbar -->
</template>

<script setup>
import { useMenu } from "@/composables/useMenu";

const menu = useMenu();
const route = useRoute();

const isMenuOpen = ref(false);

// Track open submenu indices
const openIndexes = ref({
  main: null,
  child: null,
});

const toggleMain = (index) => {
  openIndexes.value.main = openIndexes.value.main === index ? null : index;
  openIndexes.value.child = null; // close child submenu
};

const toggleChild = (index) => {
  openIndexes.value.child = openIndexes.value.child === index ? null : index;
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  isMenuOpen.value = false;
};

const handleScroll = () => {
  const navbar = document.getElementById("topnav");
  if (navbar) {
    if (
      document.body.scrollTop >= 50 ||
      document.documentElement.scrollTop >= 50
    ) {
      navbar.classList.add("nav-sticky");
    } else {
      navbar.classList.remove("nav-sticky");
    }
  }
};

const handleEsc = (e) => {
  if (e.key === "Escape" && isMenuOpen.value) {
    closeMenu();
  }
};

// Close drawer on route change
watch(
  () => route.fullPath,
  () => {
    isMenuOpen.value = false;
  }
);

// Lock body scroll while drawer is open; reset submenu state on close
watch(isMenuOpen, (open) => {
  if (import.meta.client) {
    document.body.style.overflow = open ? "hidden" : "";
  }
  if (!open) {
    openIndexes.value.main = null;
    openIndexes.value.child = null;
  }
});

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("keydown", handleEsc);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("keydown", handleEsc);
  if (import.meta.client) {
    document.body.style.overflow = "";
  }
});
</script>
